import { useState, useEffect } from 'react'
import { ChevronDown, ChevronUp, Plus, Trash2, GripVertical, Hash } from 'lucide-react'
import QuestionEditor from './QuestionEditor'
import { generateUniqueId } from '../../utils/questionnaireValidation'
import { slugify } from '../../utils/idGenerator'

/**
 * ThemeEditor Component
 * Edits a theme with its questions
 */
function ThemeEditor({ theme, index, onUpdate, onDelete, allThemeIds }) {
  const [isExpanded, setIsExpanded] = useState(true)

  // Generate the theme ID from the title once, for a brand new theme.
  // An existing id is never rewritten: it is the key past assessments were
  // stored with, and changing it breaks their comparison.
  useEffect(() => {
    if (!theme.id && theme.title) {
      const generatedId = slugify(theme.title)
      if (generatedId) {
        onUpdate({ ...theme, id: generatedId })
      }
    }
  }, [theme.title])

  const handleChange = (field, value) => {
    onUpdate({ ...theme, [field]: value })
  }

  const handleQuestionUpdate = (questionIndex, updatedQuestion) => {
    const updatedQuestions = [...theme.questions]
    updatedQuestions[questionIndex] = updatedQuestion
    onUpdate({ ...theme, questions: updatedQuestions })
  }

  const handleAddQuestion = () => {
    const existingQuestionIds = theme.questions.map(q => q.id)
    const newQuestionId = generateUniqueId(`q${index + 1}`, existingQuestionIds)
    
    const newQuestion = {
      id: newQuestionId,
      text: '',
      description: '', // Add description field
      weight: 1
    }
    
    onUpdate({ ...theme, questions: [...theme.questions, newQuestion] })
  }

  const handleDeleteQuestion = (questionIndex) => {
    if (theme.questions.length <= 1) {
      alert('Un thème doit contenir au moins une question')
      return
    }
    
    const updatedQuestions = theme.questions.filter((_, i) => i !== questionIndex)
    onUpdate({ ...theme, questions: updatedQuestions })
  }

  // Calculate theme max score
  const themeMaxScore = theme.questions.reduce((sum, q) => sum + (q.weight * 10), 0)

  return (
    <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
      {/* Theme Header */}
      <div className="bg-gray-100 border-b border-gray-200 p-4">
        <div className="flex items-center space-x-3">
          {/* Drag handle */}
          <div className="flex-shrink-0 text-gray-400 cursor-move">
            <GripVertical className="w-5 h-5" />
          </div>

          {/* Theme number badge */}
          <div className="flex-shrink-0">
            <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
              Thème #{index + 1}
            </span>
          </div>

          {/* Theme title */}
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-gray-900">
              {theme.title || 'Nouveau thème'}
            </h3>
            <p className="text-sm text-gray-500">
              {theme.questions.length} question(s) • Score max: {themeMaxScore}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
              title={isExpanded ? 'Réduire' : 'Développer'}
            >
              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Supprimer le thème"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Theme Fields (Collapsible) */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Theme ID (generated once, read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
              <Hash className="w-4 h-4 mr-1" />
              ID du thème (permanent)
            </label>
            <input
              type="text"
              value={theme.id}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-500">
              🔒 L'ID est attribué à la création du thème et ne change plus : les évaluations déjà
              soumises y sont rattachées. Renommer le thème ne le modifie pas.
            </p>
          </div>

          {/* Theme title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titre du thème *
            </label>
            <input
              type="text"
              value={theme.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="ex: Stratégie et Vision"
              required
            />
          </div>

          {/* Theme icon */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Icône (emoji) *
            </label>
            <input
              type="text"
              value={theme.icon}
              onChange={(e) => handleChange('icon', e.target.value)}
              className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-2xl text-center"
              placeholder="🎯"
              maxLength="2"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Utilisez un emoji pour représenter le thème
            </p>
          </div>

          {/* Theme description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              value={theme.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows="3"
              placeholder="Description du thème..."
              required
            />
          </div>

          {/* Questions section */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-md font-semibold text-gray-900">
                Questions ({theme.questions.length})
              </h4>
              <button
                type="button"
                onClick={handleAddQuestion}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Ajouter une question</span>
              </button>
            </div>

            {/* Questions list */}
            <div className="space-y-3">
              {theme.questions.map((question, qIndex) => (
                <QuestionEditor
                  key={qIndex}
                  question={question}
                  index={qIndex}
                  themeIndex={index}
                  onUpdate={(updated) => handleQuestionUpdate(qIndex, updated)}
                  onDelete={() => handleDeleteQuestion(qIndex)}
                  isFirst={qIndex === 0}
                  isLast={qIndex === theme.questions.length - 1}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ThemeEditor

